import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { 
  GraduationCap, 
  Target,
  TrendingUp,
  Loader2,
  LogOut,
  BookOpen,
  Heart,
  Brain,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHART_DOMAIN = [0, 100];
const CHART_TOOLTIP_STYLE = { borderRadius: '12px', border: '1px solid hsl(210, 30%, 92%)', fontSize: '13px' };
const CHART_DOT = { fill: 'hsl(199, 89%, 48%)', r: 4 };
const CHART_ACTIVE_DOT = { r: 6, fill: 'hsl(199, 89%, 48%)' };

const StudentDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const contentRes = await fetch(`${API}/student/content`);
      if (contentRes.ok) { const data = await contentRes.json(); setContent(data.content); }
    } catch (error) { logger.error('Failed to fetch data:', error); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = async () => { await logout(); toast.success('Logged out'); navigate('/'); };
  const getInitials = (name) => name?.split(' ').map((n) => n[0]).join('').toUpperCase() || 'S';

  const getLevelColor = (level) => {
    switch (level) {
      case 'Excellent': return 'text-white bg-black';
      case 'Very Good': return 'text-[hsl(199,89%,48%)] bg-plum-voltage/10';
      case 'Good': return 'text-amber-600 bg-amber-spark/10';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-plum-voltage/10 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(199,89%,48%)]" />
      </div>
    );
  }

  const latestResult = results[0];
  const chartData = results.slice().reverse().map((r, i) => ({ name: `Test ${i + 1}`, score: r.percentage }));

  const contentTopics = [
    { key: 'understanding_emotions', icon: Brain, color: 'bg-[hsl(199,20%,92%)] text-[hsl(199,89%,48%)]' },
    { key: 'managing_stress', icon: Heart, color: 'bg-rose-100 text-rose-600' },
    { key: 'friendships', icon: Sparkles, color: 'bg-plum-voltage/15 text-plum-voltage' },
    { key: 'social_media', icon: Target, color: 'bg-amber-100 text-amber-600' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-white to-[hsl(210,40%,96%)]">
      {/* Student Header — Purple accent */}
      <header className="bg-black/80 backdrop-blur-xl border-b border-[hsl(210,30%,90%)] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="student-logo">
            <div className="w-10 h-10 rounded-xl bg-[hsl(199,89%,48%)] flex items-center justify-center">
              <GraduationCap className="h-5 w-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <span className="font-semibold text-foreground">Mindpuls<span className="underline decoration-slate-600 decoration-2 underline-offset-2">eI</span></span>
              <span className="text-xs text-[hsl(199,89%,48%)] block font-medium">Student Portal</span>
            </div>
          </Link>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full p-1" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-[hsl(199,30%,85%)] text-[hsl(199,70%,35%)] text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <div className="px-3 py-2">
                  <p className="font-medium text-sm">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.grade} &middot; Age {user?.age}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-btn">
                  <LogOut className="h-4 w-4 mr-2" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8" data-testid="student-dashboard">
        {/* Welcome Banner */}
        <div className="relative rounded-3xl overflow-hidden mb-8 p-8 bg-[hsl(199,89%,48%)] text-white animate-fade-in">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-black rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
          </div>
          <div className="relative flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-1">Hey {user?.name?.split(' ')[0]}!</h1>
              <p className="text-white/70 text-sm">Ready to level up your emotional skills?</p>
            </div>
            <div className="hidden sm:block">
              <img src="https://images.unsplash.com/photo-1758270705639-9727f350f026?crop=entropy&cs=srgb&fm=jpg&q=85&w=200" alt="Students" className="w-24 h-24 rounded-2xl object-cover border-2 border-white/20" />
            </div>
          </div>
        </div>

        {/* Score & Quick Start */}
        <div className="grid md:grid-cols-3 gap-5 mb-8">
          <Card className="rounded-3xl border-[hsl(210,30%,90%)] shadow-ambient md:col-span-2 bg-black/80 backdrop-blur" data-testid="score-card">
            <CardContent className="p-7">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Your EI Score</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-[hsl(199,89%,48%)] tabular-nums">{latestResult?.percentage?.toFixed(0) || '--'}</span>
                    <span className="text-lg text-muted-foreground">/100</span>
                  </div>
                </div>
                {latestResult?.level && (
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(latestResult.level)}`}>{latestResult.level}</span>
                )}
              </div>

              {latestResult?.category_scores && (
                <div className="space-y-3">
                  {Object.entries(latestResult.category_scores).slice(0, 4).map(([category, score]) => (
                    <div key={category}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="capitalize font-medium">{category.replace('_', ' ')}</span>
                        <span className="text-muted-foreground tabular-nums">{score}%</span>
                      </div>
                      <div className="h-2 bg-[hsl(210,30%,93%)] rounded-full overflow-hidden">
                        <div className="h-full bg-[hsl(199,89%,48%)] rounded-full" style={{ width: `${score}%`, transition: 'width 1s ease' }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {!latestResult && (
                <div className="text-center py-6">
                  <p className="text-muted-foreground text-sm">No results yet</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card className="rounded-3xl border-[hsl(210,30%,90%)] shadow-ambient bg-black/80" data-testid="tests-completed">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="w-11 h-11 rounded-2xl bg-[hsl(199,20%,92%)] flex items-center justify-center">
                    <Target className="h-5 w-5 text-[hsl(199,89%,48%)]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold tabular-nums">{results.length}</p>
                    <p className="text-sm text-muted-foreground">Tests Done</p>
                  </div>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>

        {/* Progress Chart */}
        {chartData.length > 0 && (
          <Card className="rounded-3xl border-[hsl(210,30%,90%)] shadow-ambient mb-8 bg-black/80" data-testid="progress-chart">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <TrendingUp className="h-4 w-4 text-[hsl(199,89%,48%)]" /> Your Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(210, 30%, 92%)" />
                    <XAxis dataKey="name" stroke="hsl(199, 89%, 48%)" fontSize={12} />
                    <YAxis domain={CHART_DOMAIN} stroke="hsl(199, 89%, 48%)" fontSize={12} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                    <Line type="monotone" dataKey="score" stroke="hsl(199, 89%, 48%)" strokeWidth={2.5} dot={CHART_DOT} activeDot={CHART_ACTIVE_DOT} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Learning Content */}
        <Card className="rounded-3xl border-[hsl(210,30%,90%)] shadow-ambient mb-8 bg-black/80" data-testid="learning-content">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <BookOpen className="h-4 w-4 text-[hsl(199,89%,48%)]" /> Learn About Emotions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 gap-4">
              {contentTopics.map(({ key, icon: Icon, color }) => {
                const topic = content[key];
                if (!topic) return null;
                return (
                  <Link key={key} to={`/student/learn/${key}`}>
                    <Card className="rounded-2xl border-[hsl(210,30%,93%)] card-hover h-full bg-black">
                      <CardContent className="p-5">
                        <div className={`w-10 h-10 rounded-xl ${color} flex items-center justify-center mb-3`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <h3 className="font-semibold text-sm mb-1">{topic.title}</h3>
                        <p className="text-xs text-muted-foreground line-clamp-2">{topic.description}</p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>

      </main>
    </div>
  );
};

export default StudentDashboard;
